import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiPageContextSync } from '@/components/ai-agent/AiPageContextSync';
import {
  Card,
  Button,
  Space,
  Descriptions,
  Table,
  Typography,
  Tag,
  App,
  Spin,
  Popconfirm,
  Collapse,
} from 'antd';
import { ArrowLeftOutlined, EditOutlined, CheckCircleOutlined, StopOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  usePaymentVoucher,
  usePostPaymentVoucher,
  useCancelPaymentVoucher,
  usePaymentVoucherSettlementHistory,
  usePaymentVoucherAdvanceAllocations,
} from '@/hooks/usePaymentVoucher';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import paymentVoucherService from '@/services/paymentVoucher.service';
import type {
  PaymentVoucher,
  PaymentVoucherAllocationLine,
  PaymentVoucherAdvanceApplication,
  SettlementLineInput,
} from '@/services/paymentVoucher.service';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import { InvoiceNumberWithNarration } from '@/components/finance/InvoiceNumberWithNarration';
import { DocumentRefLink } from '@/components/DocumentRefLink';
import { firstNonUuidDisplay } from '@/utils/displayLabels';
import { useDocumentBreadcrumb } from '@/contexts/PageBreadcrumbContext';
import { useQueryClient } from '@tanstack/react-query';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { TaskRoutingCard } from '@/components/unified/TaskRoutingCard';
import { ENTITY_TYPE_PAYMENT_VOUCHER } from '@/constants/approval-entity-types';
import ProcurementAttachmentsCard from '@/components/procurement/ProcurementAttachmentsCard';
import { DOCUMENT_ENTITY_TYPE_PAYMENT_VOUCHER } from '@/constants/document-entity-types';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';

const { Title, Text } = Typography;

const TRANSFER_TYPES = ['BankTransfer', 'CashTransfer', 'AccountTransfer'];

const PaymentVoucherDetailPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const paymentTypeLabel = (value?: string) => {
    const map: Record<string, string> = {
      SupplierBillPayment: t('paymentTypeSupplierBillPayment'),
      SupplierAdvance: t('paymentTypeSupplierAdvance'),
      EmployeePayment: t('paymentTypeEmployeePayment'),
      ExpensePayment: t('paymentTypeExpensePayment'),
      DirectPayment: t('paymentTypeDirectPayment'),
      BankTransfer: t('paymentTypeBankTransfer'),
      CashTransfer: t('paymentTypeCashTransfer'),
      AccountTransfer: t('paymentTypeAccountTransfer'),
    };
    return (value && map[value]) || value || '—';
  };
  const paymentModeLabel = (value?: string) => {
    const map: Record<string, string> = {
      Cash: t('modeCash'),
      Cheque: t('modeCheque'),
      BankTransfer: t('modeBankTransfer'),
      Online: t('modeOnline'),
      Card: t('modeCard'),
      PDC: t('modePdc'),
      Other: t('modeOther'),
    };
    return (value && map[value]) || value || '—';
  };
  const statusLabel = (status: string) => {
    const map: Record<string, string> = {
      Draft: t('statusDraft'),
      Submitted: tc('submitted'),
      Approved: t('statusApproved'),
      Posted: t('statusPosted'),
      Cancelled: t('statusCancelled'),
    };
    return map[status] ?? status;
  };
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { formatCurrency } = useCurrency();
  const [pvActionLoading, setPvActionLoading] = useState<string | null>(null);

  const { data: pv, isLoading } = usePaymentVoucher(id);
  const { data: departments = [] } = useDepartmentsForSelection();
  const { data: settlementHistory } = usePaymentVoucherSettlementHistory(
    pv?.payment_type === 'SupplierBillPayment' ? id : undefined
  );
  const { data: advanceApplications = [] } = usePaymentVoucherAdvanceAllocations(id, pv?.payment_type);
  const postMutation = usePostPaymentVoucher();
  const cancelMutation = useCancelPaymentVoucher();

  const breadcrumbSegments = useMemo(
    () =>
      pv
        ? [
            { title: t('financeModule'), path: '/fin/dashboard' },
            { title: t('paymentVouchers'), path: '/fin/payment-vouchers' },
            { title: pv.voucher_number || t('payment') },
          ]
        : null,
    [pv?.id, pv?.voucher_number, t]
  );
  useDocumentBreadcrumb(breadcrumbSegments);

  const pvWorkflowStages = useMemo(() => {
    if (!pv) return [];
    const st = pv.status;
    const cancelled = st === 'Cancelled';
    const posted = st === 'Posted';
    return [
      {
        title: t('statusDraft'),
        description: t('workflowEntry'),
        status: (cancelled ? 'error' : st === 'Draft' ? 'process' : 'finish') as 'wait' | 'process' | 'finish' | 'error',
      },
      {
        title: t('statusPosted'),
        description: t('workflowGl'),
        status: (cancelled ? 'wait' : posted ? 'finish' : 'wait') as 'wait' | 'process' | 'finish' | 'error',
      },
    ];
  }, [pv, t]);

  const pvWorkflowCurrent = useMemo(() => {
    if (!pv || pv.status === 'Cancelled') return 0;
    if (pv.status === 'Draft') return 0;
    if (pv.status === 'Posted') return 1;
    return 0;
  }, [pv]);

  const allocationLines = settlementHistory?.allocation_lines ?? [];
  const allocationTableRows: PaymentVoucherAllocationLine[] = useMemo(() => {
    if (!pv) return [];
    if (allocationLines.length) return allocationLines;
    const snapshot = (pv as PaymentVoucher & { settlement_snapshot?: SettlementLineInput[] }).settlement_snapshot;
    if (snapshot?.length) {
      return snapshot.map((line) => {
        const cash = Number(line.cash_amount ?? 0);
        const adv = (line.advance_adjustments ?? []).reduce((s, a) => s + Number(a.amount ?? 0), 0);
        const dn = (line.debit_note_adjustments ?? []).reduce((s, d) => s + Number(d.amount ?? 0), 0);
        const inv = (pv.allocations ?? []).find((a) => a.supplier_invoice_id === line.supplier_invoice_id)?.supplierInvoice;
        return {
          supplier_invoice_id: line.supplier_invoice_id,
          invoice_number: inv?.invoice_number ?? line.supplier_invoice_id,
          supplier_invoice_number: inv?.supplier_invoice_number,
          cash_amount: cash,
          advance_adjustment: adv,
          debit_note_adjustment: dn,
          line_total: cash + adv + dn,
          advance_details: line.advance_adjustments,
          debit_note_details: line.debit_note_adjustments,
        };
      });
    }
    return (pv.allocations ?? []).map((a) => ({
      supplier_invoice_id: a.supplier_invoice_id,
      invoice_number: a.supplierInvoice?.invoice_number ?? a.supplier_invoice_id,
      supplier_invoice_number: a.supplierInvoice?.supplier_invoice_number,
      cash_amount: Number(a.allocated_amount),
      advance_adjustment: 0,
      debit_note_adjustment: 0,
      line_total: Number(a.allocated_amount),
      advance_details: [],
      debit_note_details: [],
    }));
  }, [allocationLines, pv]);

  if (!id) {
    navigate('/fin/payment-vouchers');
    return null;
  }
  if (isLoading && !pv) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
      {id ? <AiPageContextSync module="finance" docType="PaymentVoucher" docId={id} /> : null}
        <Spin size="large" />
      </div>
    );
  }
  if (!pv) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <Text type="secondary">{t('paymentVoucherNotFound')}</Text>
        </Card>
      </div>
    );
  }

  const isDraft = pv.status === 'Draft';
  const isPosted = pv.status === 'Posted';
  const isSubmitted = pv.status === 'Submitted';
  const isApprovedPv = pv.status === 'Approved';
  const isCancelled = pv.status === 'Cancelled';
  const canCancel = !isCancelled && ['Draft', 'Submitted', 'Approved', 'Posted'].includes(pv.status);
  const isTransfer = TRANSFER_TYPES.includes(pv.payment_type);
  const showUniversalApproval = !!pv.approval_instance_id;

  const runPvAction = async (key: string, fn: () => Promise<unknown>) => {
    setPvActionLoading(key);
    try {
      await fn();
      message.success(tc('done'));
      queryClient.invalidateQueries({ queryKey: ['payment-vouchers'] });
      queryClient.invalidateQueries({ queryKey: ['payment-vouchers', id] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || t('actionFailed'));
    } finally {
      setPvActionLoading(null);
    }
  };

  const headerActions = (
    <Space wrap size="small">
      <Button type="default" icon={<ArrowLeftOutlined />} onClick={() => navigate('/fin/payment-vouchers')}>
        {tc('back')}
      </Button>
      <ReportDocumentActions
        reportKey="payment-voucher"
        params={id ? { id } : null}
        documentId={id}
        sendPermissions={['finance.voucher.create']}
        emailLabel={t('email')}
      />
      {isPosted && (
        <>
          <ReportDocumentActions
            reportKey="payment-receipt"
            params={id ? { id } : null}
            documentId={id}
            sendPermissions={['finance.voucher.create']}
            emailLabel={t('emailReceipt')}
          />
          <ReportDocumentActions
            reportKey="accounting-voucher"
            params={id ? { source_type: 'payment_voucher', source_id: id } : null}
            documentId={id}
            sendPermissions={['finance.voucher.create']}
            emailLabel={t('emailGl')}
          />
        </>
      )}
      {isDraft && (
        <>
          <Button icon={<EditOutlined />} onClick={() => navigate(`/fin/payment-vouchers/${id}/edit`)}>
            {tc('edit')}
          </Button>
          <Button
            loading={pvActionLoading === 'submit-approval'}
            onClick={() => runPvAction('submit-approval', () => paymentVoucherService.submitForApproval(id!))}
          >
            {t('submitForApproval')}
          </Button>
          <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => postMutation.mutate(id!)} loading={postMutation.isPending}>
            {tc('post')}
          </Button>
        </>
      )}
      {isSubmitted && !showUniversalApproval && (
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          loading={pvActionLoading === 'approve-doc'}
          onClick={() => runPvAction('approve-doc', () => paymentVoucherService.approveDocument(id!))}
        >
          {tc('approve')}
        </Button>
      )}
      {isApprovedPv && (
        <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => postMutation.mutate(id!)} loading={postMutation.isPending}>
          {tc('post')}
        </Button>
      )}
      {canCancel && (
        <Popconfirm
          title={t('cancelThisPaymentVoucher')}
          description={
            isPosted
              ? t('cancelPvPostedDescLong')
              : t('cancelPvDraftDescLong')
          }
          onConfirm={() => cancelMutation.mutate(id!)}
          okText={t('cancelVoucher')}
          cancelText={tc('keep')}
        >
          <Button danger icon={<StopOutlined />} loading={cancelMutation.isPending}>
            {tc('cancel')}
          </Button>
        </Popconfirm>
      )}
    </Space>
  );

  const rightRail = (
    <div>
      <Title level={5}>{t('quickLinks')}</Title>
      <Button type="link" block style={{ textAlign: 'left', paddingLeft: 0 }} onClick={() => navigate('/fin/payment-vouchers')}>
        {t('allPaymentVouchers')}
      </Button>
    </div>
  );

  const collapseItems: { key: string; label: React.ReactNode; children: React.ReactNode }[] = [
    {
      key: 'header',
      label: t('voucherDetailsSection'),
      children: (
        <div>
          <Card bordered={false} styles={{ body: { padding: 0 } }}>
            <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
              <Descriptions.Item label={t('voucherDate')}>{pv.voucher_date ? dayjs(pv.voucher_date).format('DD MMM YYYY') : '-'}</Descriptions.Item>
              <Descriptions.Item label={t('department')}>
                {pv.department_id
                  ? (() => {
                      const d = departments.find((x) => x.id === pv.department_id);
                      return d ? `${d.department_code} - ${d.department_name}` : pv.department_id;
                    })()
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('paymentType')}>{paymentTypeLabel(pv.payment_type)}</Descriptions.Item>
              <Descriptions.Item label={t('payee')}>
                {(() => {
                  const isMulti = pv.settlement_scope === 'MultiSupplier' || (!pv.supplier_id && pv.payment_type === 'SupplierBillPayment');
                  if (isMulti) {
                    const n = new Set(
                      (pv.allocations ?? [])
                        .map((a) => a.supplier_id || a.supplierInvoice?.supplier_id)
                        .filter(Boolean)
                    ).size;
                    return pv.payee_name || t('multiSupplierN', { count: n || 'N' });
                  }
                  return (
                    (pv as PaymentVoucher & { supplier?: { supplier_name?: string } }).supplier?.supplier_name ??
                    pv.payee_name ??
                    '-'
                  );
                })()}
              </Descriptions.Item>
              {pv.payment_type === 'EmployeePayment' && pv.leave_settlement_id && (
                <Descriptions.Item label={t('leaveSettlement')}>
                  <a href={`/hr/leave-settlements/${pv.leave_settlement_id}`}>{t('openLeaveSettlement')}</a>
                </Descriptions.Item>
              )}
              {pv.payment_type === 'EmployeePayment' && pv.destination_account_id && (
                <Descriptions.Item label={t('employeePayableAccount')}>
                  {(pv as PaymentVoucher & { destinationAccount?: { account_code: string; account_name: string } })
                    .destinationAccount
                    ? `${(pv as any).destinationAccount.account_code} - ${(pv as any).destinationAccount.account_name}`
                    : pv.destination_account_id}
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t('currency')}>{pv.currency ?? 'AED'}</Descriptions.Item>
              <Descriptions.Item label={t('principalAmount')}>{formatCurrency(Number(pv.amount), pv.currency)}</Descriptions.Item>
              {(pv.chargeLines?.length ?? 0) > 0 && (
                <>
                  <Descriptions.Item label={t('additionalCharges')}>
                    {formatCurrency(
                      (pv.chargeLines ?? []).reduce((s, l) => s + Number(l.amount), 0),
                      pv.currency
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('totalBankCashOutflow')}>
                    {formatCurrency(
                      Number(pv.amount) + (pv.chargeLines ?? []).reduce((s, l) => s + Number(l.amount), 0),
                      pv.currency
                    )}
                  </Descriptions.Item>
                </>
              )}
              <Descriptions.Item label={t('paymentMode')}>{paymentModeLabel(pv.payment_mode)}</Descriptions.Item>
              <Descriptions.Item label={t('paymentAccount')}>
                {(pv as PaymentVoucher & { paymentAccount?: { account_code: string; account_name: string } }).paymentAccount
                  ? `${(pv as any).paymentAccount.account_code} - ${(pv as any).paymentAccount.account_name}`
                  : firstNonUuidDisplay(null)}
              </Descriptions.Item>
              {pv.payment_type === 'SupplierAdvance' && pv.destination_account_id && (
                <Descriptions.Item label={t('advanceGlAccount')}>
                  {(pv as PaymentVoucher & { destinationAccount?: { account_code: string; account_name: string } }).destinationAccount
                    ? `${(pv as any).destinationAccount.account_code} - ${(pv as any).destinationAccount.account_name}`
                    : firstNonUuidDisplay(null)}
                </Descriptions.Item>
              )}
              {isTransfer && pv.destination_account_id && (
                <Descriptions.Item label={t('destinationAccount')}>
                  {(pv as PaymentVoucher & { destinationAccount?: { account_code: string; account_name: string } }).destinationAccount
                    ? `${(pv as any).destinationAccount.account_code} - ${(pv as any).destinationAccount.account_name}`
                    : firstNonUuidDisplay(null)}
                </Descriptions.Item>
              )}
              {pv.purchase_order_id && (
                <Descriptions.Item label={t('purchaseOrder')}>
                  <DocumentRefLink
                    to={`/scm/procurement/purchase-orders/${pv.purchase_order_id}`}
                    number={pv.purchase_order_number}
                    id={pv.purchase_order_id}
                  />
                </Descriptions.Item>
              )}
              {pv.po_milestone_id && (
                <Descriptions.Item label={t('poMilestone')}>
                  {firstNonUuidDisplay(pv.po_milestone_label)}
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t('reference')}>{pv.reference_number ?? '-'}</Descriptions.Item>
              <Descriptions.Item label={t('narration')} span={2}>{pv.narration ?? '-'}</Descriptions.Item>
              {pv.cheque_number && <Descriptions.Item label={t('chequeNo')}>{pv.cheque_number}</Descriptions.Item>}
              {pv.cheque_date && <Descriptions.Item label={t('chequeDate')}>{dayjs(pv.cheque_date).format('DD MMM YYYY')}</Descriptions.Item>}
              {pv.bank_name && <Descriptions.Item label={t('bank')}>{pv.bank_name}</Descriptions.Item>}
              {pv.transaction_reference && <Descriptions.Item label={t('transactionRef')}>{pv.transaction_reference}</Descriptions.Item>}
              {pv.payment_type === 'SupplierBillPayment' && (
                <>
                  <Descriptions.Item label={t('discount')}>{formatCurrency(Number(pv.discount_amount ?? 0))}</Descriptions.Item>
                  <Descriptions.Item label={t('writeOff')}>{formatCurrency(Number(pv.write_off_amount ?? 0))}</Descriptions.Item>
                  {(pv as PaymentVoucher & { settlement_remarks?: string }).settlement_remarks && (
                    <Descriptions.Item label={t('settlementRemarks')} span={2}>
                      {(pv as PaymentVoucher & { settlement_remarks?: string }).settlement_remarks}
                    </Descriptions.Item>
                  )}
                </>
              )}
            </Descriptions>
          </Card>
        </div>
      ),
    },
  ];
  const pvCurrency = pv.currency ?? 'AED';
  const fmtAmt = (amount: number) => formatCurrency(amount, pvCurrency);

  if (pv.payment_type === 'SupplierBillPayment') {
    collapseItems.push({
      key: 'allocation',
      label: t('allocationSettlementDetails'),
      children: (
        <div>
          {allocationTableRows.length === 0 ? (
            <Text type="secondary">{t('noSupplierInvoicesAllocated')}</Text>
          ) : (
            <>
          {settlementHistory?.summary && (
            <Descriptions size="small" column={3} style={{ marginBottom: 12 }}>
              <Descriptions.Item label={t('cashPayableLabel')}>{fmtAmt(settlementHistory.summary.net_cash_payable)}</Descriptions.Item>
              <Descriptions.Item label={t('advanceAdjustment')}>{fmtAmt(settlementHistory.summary.total_advance_adjustment)}</Descriptions.Item>
              <Descriptions.Item label={t('debitNoteAdjustment')}>{fmtAmt(settlementHistory.summary.total_debit_note_adjustment)}</Descriptions.Item>
              <Descriptions.Item label={t('discount')}>{fmtAmt(settlementHistory.summary.discount_amount)}</Descriptions.Item>
              <Descriptions.Item label={t('writeOff')}>{fmtAmt(settlementHistory.summary.write_off_amount)}</Descriptions.Item>
            </Descriptions>
          )}
          <Table
            size="small"
            rowKey="supplier_invoice_id"
            dataSource={allocationTableRows}
            pagination={false}
            expandable={
              allocationLines.length
                ? {
                    expandedRowRender: (row: PaymentVoucherAllocationLine) => (
                      <div style={{ padding: '4px 0' }}>
                        {(row.advance_details?.length ?? 0) > 0 && (
                          <div style={{ marginBottom: 8 }}>
                            <Text strong>{t('advanceAdjustments')}: </Text>
                            {row.advance_details!.map((d, i) => (
                              <span key={i}>
                                {i > 0 ? '; ' : ''}
                                {firstNonUuidDisplay(d.advance_voucher_number)} {fmtAmt(d.amount)}
                              </span>
                            ))}
                          </div>
                        )}
                        {(row.debit_note_details?.length ?? 0) > 0 && (
                          <div>
                            <Text strong>{t('debitNoteAdjustments')}: </Text>
                            {row.debit_note_details!.map((d, i) => (
                              <span key={i}>
                                {i > 0 ? '; ' : ''}
                                {firstNonUuidDisplay(d.debit_note_number)} {fmtAmt(d.amount)}
                              </span>
                            ))}
                          </div>
                        )}
                        {!row.advance_details?.length && !row.debit_note_details?.length && (
                          <Text type="secondary">{t('cashAllocationOnly')}</Text>
                        )}
                      </div>
                    ),
                    rowExpandable: (row: PaymentVoucherAllocationLine) =>
                      (row.advance_details?.length ?? 0) > 0 || (row.debit_note_details?.length ?? 0) > 0,
                  }
                : undefined
            }
            columns={[
              {
                title: t('invoice'),
                dataIndex: 'invoice_number',
                key: 'invoice_number',
                render: (v: string, row: PaymentVoucherAllocationLine) => (
                  <InvoiceNumberWithNarration number={v} narration={row.narration} />
                ),
              },
              {
                title: t('supplierInvHash'),
                dataIndex: 'supplier_invoice_number',
                key: 'supplier_invoice_number',
                render: (v: string) => v ?? '—',
              },
              {
                title: t('outstanding'),
                dataIndex: 'balance_due',
                key: 'balance_due',
                align: 'right' as const,
                render: (v: number | undefined) => (v != null ? fmtAmt(v) : '—'),
              },
              {
                title: t('cash'),
                dataIndex: 'cash_amount',
                key: 'cash_amount',
                align: 'right' as const,
                render: (v: number) => fmtAmt(Number(v ?? 0)),
              },
              {
                title: t('advance'),
                dataIndex: 'advance_adjustment',
                key: 'advance_adjustment',
                align: 'right' as const,
                render: (v: number) => fmtAmt(Number(v ?? 0)),
              },
              {
                title: t('debitNote'),
                dataIndex: 'debit_note_adjustment',
                key: 'debit_note_adjustment',
                align: 'right' as const,
                render: (v: number) => fmtAmt(Number(v ?? 0)),
              },
              {
                title: t('lineTotal'),
                dataIndex: 'line_total',
                key: 'line_total',
                align: 'right' as const,
                render: (v: number, row: PaymentVoucherAllocationLine) =>
                  fmtAmt(Number(v ?? row.cash_amount ?? 0)),
              },
            ]}
          />
            </>
          )}
        </div>
      ),
    });
  }
  if (pv.payment_type === 'SupplierAdvance') {
    const sarLines = pv.advanceAllocations ?? [];
    collapseItems.push({
      key: 'advance-settlement',
      label: t('supplierAdvanceRequestsPaid'),
      children: (
        <div>
          {sarLines.length === 0 ? (
            <Text type="secondary">
              {pv.supplier_advance_request_id
                ? t('linkedSingleAdvanceLegacy')
                : t('noAdvanceRequestLines')}
            </Text>
          ) : (
            <Table
              size="small"
              rowKey={(r) => r.id ?? r.supplier_advance_request_id}
              dataSource={sarLines}
              pagination={false}
              columns={[
                {
                  title: t('advanceRequest'),
                  key: 'sar',
                  render: (_: unknown, r: NonNullable<typeof pv.advanceAllocations>[number]) =>
                    r.supplierAdvanceRequest?.advance_request_number
                      ? r.supplierAdvanceRequest.advance_request_number
                      : firstNonUuidDisplay(null),
                },
                {
                  title: t('amount'),
                  dataIndex: 'allocated_amount',
                  align: 'right' as const,
                  render: (v: number) => formatCurrency(Number(v), pvCurrency),
                },
              ]}
            />
          )}
        </div>
      ),
    });
    collapseItems.push({
      key: 'advance-applied',
      label: t('appliedToSupplierInvoices'),
      children: (
        <div>
          {advanceApplications.length === 0 ? (
            <Text type="secondary">{t('advanceNotAppliedYet')}</Text>
          ) : (
            <Table<PaymentVoucherAdvanceApplication>
              size="small"
              rowKey={(r) => `${r.supplier_invoice_id}-${r.adjustment_date}`}
              dataSource={advanceApplications}
              pagination={false}
              columns={[
                {
                  title: t('invoice'),
                  dataIndex: 'invoice_number',
                  key: 'invoice_number',
                  render: (v: string, row: PaymentVoucherAdvanceApplication) => (
                    <InvoiceNumberWithNarration number={v} narration={row.narration} />
                  ),
                },
                {
                  title: t('supplierInvHash'),
                  dataIndex: 'supplier_invoice_number',
                  key: 'supplier_invoice_number',
                  render: (v: string) => v ?? '—',
                },
                {
                  title: t('amountApplied'),
                  dataIndex: 'allocated_amount',
                  key: 'allocated_amount',
                  align: 'right' as const,
                  render: (v: number, row: PaymentVoucherAdvanceApplication) =>
                    formatCurrency(Number(v), row.currency ?? pvCurrency),
                },
                {
                  title: t('adjustmentDate'),
                  dataIndex: 'adjustment_date',
                  key: 'adjustment_date',
                  render: (v: string) => (v ? dayjs(v).format('DD MMM YYYY') : '—'),
                },
              ]}
            />
          )}
        </div>
      ),
    });
  }
  if ((pv.chargeLines?.length ?? 0) > 0) {
    collapseItems.push({
      key: 'charges',
      label: t('additionalCharges'),
      children: (
        <div>
          <Table
            size="small"
            rowKey={(_, i) => `charge-${i}`}
            dataSource={pv.chargeLines}
            pagination={false}
            columns={[
              {
                title: t('account'),
                key: 'account',
                render: (_: unknown, row: { account?: { account_code: string; account_name: string }; account_id?: string }) =>
                  row.account ? `${row.account.account_code} - ${row.account.account_name}` : row.account_id,
              },
              {
                title: t('project'),
                key: 'project',
                render: (_: unknown, row: any) => {
                  if (row.project?.project_name) {
                    return row.project.project_number
                      ? `${row.project.project_number} - ${row.project.project_name}`
                      : row.project.project_name;
                  }
                  return row.project_id ?? '—';
                },
              },
              {
                title: t('amount'),
                dataIndex: 'amount',
                key: 'amount',
                align: 'right' as const,
                render: (v: number) => formatCurrency(Number(v), pv.currency),
              },
              { title: t('description'), dataIndex: 'description', key: 'description' },
            ]}
          />
        </div>
      ),
    });
  }
  if ((pv.payment_type === 'DirectPayment' || pv.payment_type === 'ExpensePayment') && pv.directLines?.length) {
    collapseItems.push({
      key: 'direct',
      label: t('directLines'),
      children: (
        <div>
          <Table
            size="small"
            rowKey={(_, i) => String(i)}
            dataSource={pv.directLines}
            pagination={false}
            columns={[
              {
                title: t('account'),
                key: 'account',
                render: (_: unknown, row: any) =>
                  row.account ? `${row.account.account_code} - ${row.account.account_name}` : row.account_id,
              },
              {
                title: t('project'),
                key: 'project',
                render: (_: unknown, row: any) =>
                  row.project
                    ? [row.project.project_number, row.project.project_name].filter(Boolean).join(' - ')
                    : row.project_id || '—',
              },
              { title: t('amount'), dataIndex: 'amount', key: 'amount', align: 'right' as const, render: (v: number) => formatCurrency(Number(v)) },
              { title: t('description'), dataIndex: 'description', key: 'description' },
            ]}
          />
        </div>
      ),
    });
  }

  return (
    <div style={{ margin: '-20px -20px 0' }}>
      {id ? <AiPageContextSync module="finance" docType="PaymentVoucher" docId={id} /> : null}
      <WorkspaceLayout
        fullBleed
        header={
          <EnterprisePageHeader
            documentLabel={t('paymentVoucher')}
            documentNumber={pv.voucher_number}
            statusTag={<Tag color={isPosted ? 'green' : isDraft ? 'default' : 'red'}>{statusLabel(pv.status)}</Tag>}
            subtitle={<Text type="secondary">{pv.voucher_date ? dayjs(pv.voucher_date).format('DD MMM YYYY') : ''}</Text>}
            facts={
              <Space wrap>
                <Text>
                  <strong>{t('amount')}:</strong> {formatCurrency(Number(pv.amount), pv.currency)} ({pv.currency ?? 'AED'})
                </Text>
                <Text>
                  <strong>{t('type')}:</strong> {paymentTypeLabel(pv.payment_type)}
                </Text>
              </Space>
            }
            primaryActions={headerActions}
          />
        }
        workflow={<WorkflowStrip stages={pvWorkflowStages} current={pvWorkflowCurrent} compact />}
        rightRail={rightRail}
      >
        <TaskRoutingCard
          sourceDocumentType="PaymentVoucher"
          sourceDocumentId={pv.id}
          nextActionUserId={(pv as { next_action_user_id?: string | null }).next_action_user_id}
          documentOwnerLabel={t('documentOwnerCreatedBy')}
          documentOwnerUserId={(pv as { created_by?: string | null }).created_by}
          disabled={pv.status === 'Cancelled' || pv.status === 'Posted'}
          invalidateQueryKeys={[['payment-vouchers', id!]]}
        />
        {showUniversalApproval && (
          <div style={{ marginBottom: 16 }}>
            <ApprovalPanel
              entityType={ENTITY_TYPE_PAYMENT_VOUCHER}
              entityId={pv.id}
              onApproveViaDocument={async (remarks) => {
                await paymentVoucherService.approveDocument(id!, remarks);
              }}
              onRejectViaDocument={async (payload) => {
                await paymentVoucherService.rejectApproval(id!, payload);
              }}
              extraInvalidateQueryKeys={[['payment-vouchers', id], ['payment-vouchers']]}
            />
          </div>
        )}
        <Collapse defaultActiveKey={collapseItems.map((i) => i.key)} items={collapseItems} />
        <ProcurementAttachmentsCard
          entityType={DOCUMENT_ENTITY_TYPE_PAYMENT_VOUCHER}
          entityId={pv.id}
          title={tc('attachments')}
          readOnly={pv.status === 'Cancelled'}
          queryKeyPrefix={['documents', 'payment-voucher']}
        />
      </WorkspaceLayout>
    </div>
  );
};

export default PaymentVoucherDetailPage;
